// ─── CMS content page (public) ───────────────────────────────────────────────
// Renders a published CMS page by slug at /page/<slug>. Content is admin-authored
// HTML; we still DOMPurify-sanitise it before injecting as a defensive default.
// ──────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { Loader } from 'lucide-react'
import { Navigation } from '../components/Navigation'
import { Footer } from '../components/Footer'
import { fetchPublicCmsPage } from '../lib/cmsApi'

export default function CmsContentPage() {
  const { slug } = useParams()
  const [page, setPage] = useState(null)
  const [status, setStatus] = useState('loading') // loading | ok | notfound | error

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    fetchPublicCmsPage(slug)
      .then(data => { if (cancelled) return; setPage(data); setStatus('ok') })
      .catch(err => {
        if (cancelled) return
        setStatus(err?.response?.status === 404 ? 'notfound' : 'error')
      })
    return () => { cancelled = true }
  }, [slug])

  useEffect(() => {
    if (page?.title) document.title = `${page.title} — WQData LIVE`
  }, [page])

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />
      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-12">
        {status === 'loading' && (
          <div className="flex justify-center py-20"><Loader className="w-6 h-6 animate-spin text-[#358ECD]" /></div>
        )}
        {status === 'notfound' && (
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-[#4D4D4D] mb-2">Page not found</h1>
            <p className="text-gray-500">This page doesn’t exist or hasn’t been published.</p>
          </div>
        )}
        {status === 'error' && (
          <div className="text-center py-20 text-red-600">Something went wrong loading this page.</div>
        )}
        {status === 'ok' && page && (
          <article>
            <h1 className="text-3xl font-bold text-[#4D4D4D] mb-6">{page.title}</h1>
            <div
              className="cms-content prose max-w-none text-[#4D4D4D]"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content || '') }}
            />
          </article>
        )}
      </main>
      <Footer />
    </div>
  )
}
